import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getComicById, getRelatedComics } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function ComicPage() {
  const { comicId } = useParams<{ comicId: string }>();
  const [comic, setComic] = useState<Comic | undefined>(undefined);
  const [relatedComics, setRelatedComics] = useState<Comic[]>([]);
  const [activeTab, setActiveTab] = useState<'info' | 'chapters'>('chapters');

  useEffect(() => {
    if (comicId) {
      const found = getComicById(comicId);
      setComic(found);
      if (found) {
        setRelatedComics(getRelatedComics(comicId));
      }
      window.scrollTo({ top: 0, behavior: 'instant' as const });
    }
  }, [comicId]);

  const formatViews = (views: number): string => {
    if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
    return views.toString();
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-4xl text-foreground-300"></i>
            </div>
            <p className="text-foreground-500 mb-4">漫画未找到</p>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩漫网`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: '2026-01-01',
    dateModified: comic.updateDate,
    image: comic.coverImage,
    url: `https://www.hanmanwang.com/comic/${comic.id}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫网',
      url: 'https://www.hanmanwang.com/',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="px-4 md:px-6 py-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  { label: '漫画分类', to: '/category/all' },
                  { label: comic.category, to: `/category/${comic.categoryId}` },
                  { label: comic.title },
                ]}
              />

              <div className="mt-6 flex flex-col lg:flex-row gap-8">
                <div className="lg:w-72 flex-shrink-0">
                  <div className="rounded-lg overflow-hidden aspect-[3/4]">
                    <img
                      src={comic.coverImage}
                      alt={`${comic.title} - 韩漫免费在线阅读`}
                      title={`${comic.title} - 韩漫网`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                      <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                        {comic.title}
                      </h1>
                      <p className="text-sm text-foreground-500 mt-2">
                        作者：{comic.author}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap ${
                      comic.status === '连载中'
                        ? 'bg-accent-100 text-accent-700'
                        : 'bg-secondary-100 text-secondary-700'
                    }`}>
                      {comic.status}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-500">
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-xs text-accent-500"></i>
                      </div>
                      <span className="font-medium text-foreground-900">{comic.rating}</span>
                      <span>分</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-eye-line text-xs"></i>
                      </div>
                      <span>{formatViews(comic.views)} 次阅读</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-time-line text-xs"></i>
                      </div>
                      <span>更新于 {formatDate(comic.updateDate)}</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-4">
                    {comic.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="mt-6 flex gap-2 border-b border-background-200">
                    <button
                      onClick={() => setActiveTab('chapters')}
                      className={`pb-3 px-4 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                        activeTab === 'chapters'
                          ? 'border-primary-500 text-primary-500'
                          : 'border-transparent text-foreground-500 hover:text-foreground-700'
                      }`}
                    >
                      章节列表 ({comic.chapters.length})
                    </button>
                    <button
                      onClick={() => setActiveTab('info')}
                      className={`pb-3 px-4 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                        activeTab === 'info'
                          ? 'border-primary-500 text-primary-500'
                          : 'border-transparent text-foreground-500 hover:text-foreground-700'
                      }`}
                    >
                      作品简介
                    </button>
                  </div>

                  {activeTab === 'chapters' && (
                    <div className="mt-4">
                      <div className="flex flex-col gap-2">
                        {comic.chapters.map((chapter) => (
                          <div
                            key={chapter.id}
                            className="flex items-center justify-between p-4 rounded-lg bg-background-50 border border-background-200/60 hover:border-primary-200/50 transition-all cursor-pointer"
                          >
                            <div>
                              <h3 className="text-sm font-medium text-foreground-900">
                                {chapter.title}
                              </h3>
                              <p className="text-xs text-foreground-500 mt-0.5">
                                {formatDate(chapter.updateDate)} · {formatViews(chapter.views)} 阅读
                              </p>
                            </div>
                            <div className="w-8 h-8 flex items-center justify-center">
                              <i className="ri-arrow-right-s-line text-lg text-foreground-400"></i>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {activeTab === 'info' && (
                    <div className="mt-4">
                      <p className="text-sm text-foreground-700 leading-relaxed">
                        {comic.description}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {relatedComics.length > 0 && (
                <section className="mt-14">
                  <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                    猜你喜欢
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                    {relatedComics.map((rc) => (
                      <Link
                        key={rc.id}
                        to={`/comic/${rc.id}`}
                        className="group cursor-pointer"
                      >
                        <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
                          <img
                            src={rc.coverImage}
                            alt={`${rc.title} - 韩漫免费在线阅读`}
                            title={`${rc.title} - 韩漫网`}
                            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                            loading="lazy"
                          />
                          <div className="absolute top-2 right-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              rc.status === '连载中'
                                ? 'bg-accent-500/90 text-white'
                                : 'bg-foreground-500/80 text-white'
                            }`}>
                              {rc.status}
                            </span>
                          </div>
                        </div>
                        <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                          {rc.title}
                        </h3>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}